/**
 * [비즈니스 목적]
 * 재사용 가능한 AI 변환 프롬프트 마스터 관리 페이지
 *
 * [워크플로우]
 * 1. 테넌트 선택 → 프롬프트 목록 및 사용 현황 조회
 * 2. 프롬프트 CRUD 작업
 *    - 생성: 새 프롬프트 등록 (tenant_code + prompt_id가 기본 키)
 *    - 조회: 프롬프트 상세 정보 표시
 *    - 수정: 프롬프트 내용 편집
 *    - 삭제: 프롬프트 삭제 (연결된 파일이 있으면 경고)
 *
 * [주요 기능]
 * 1. 프롬프트 마스터 관리
 *    - 여러 파일이 공유할 수 있는 재사용 가능한 프롬프트
 *    - 변환 프롬프트 (convert_prompt): 데이터 변환에 사용
 *    - 검증 프롬프트 (validation_prompt): 변환 결과 검증에 사용
 *    - 시트 필터링 설정 (reference_sheet_names, exclude_sheet_keywords)
 *
 * 2. 수행 방법 설정
 *    - PY: Python 스크립트 변환 (고속)
 *    - SO: Claude Sonnet 변환 (고품질)
 *    - HA: Claude Haiku 변환 (초고속)
 *
 * 3. Python 코드 편집
 *    - 테넌트 버킷에서 Python 변환 스크립트 로드/저장
 *    - 직접 편집 가능 (테넌트별 독립 관리)
 *
 * [UI 구조]
 * - 상단: 통계 카드 (전체, 활성, 사용 중인 프롬프트)
 * - 메인: 프롬프트 목록 테이블 (사용 파일 수, 적용 고객사 표시)
 * - 다이얼로그: 3개 탭
 *   - Tab 0: 기본 정보 (ID, 이름, 테넌트, 수행 방법, 시트 필터링)
 *   - Tab 1: 프롬프트 (변환, 검증 프롬프트 - 전체화면 편집 지원)
 *   - Tab 2: Python 코드 (테넌트 버킷에서 로드, 편집 가능)
 *
 * [성능 최적화]
 * - CodeEditorField를 React.memo로 분리 (리렌더링 최소화)
 * - Debounced onChange (500ms)
 * - useCallback으로 핸들러 메모이제이션
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useGlobalTenant } from '../../contexts/GlobalTenantContext';
import apiService from '../../services/api';

// ===================================================================
// CodeEditorField - 별도 컴포넌트로 분리 (리렌더링 최적화)
// ===================================================================
const CodeEditorField = React.memo(({
  label,
  field,
  value,
  onChange,
  isFullscreen,
  onToggleFullscreen,
  onSave,
  rows = 10
}) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);

  // value prop 변경시 로컬 값 동기화
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // debounced onChange
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // 즉시 로컬 업데이트

    // 기존 타이머 취소
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 500ms 후 부모 상태 업데이트
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, 500);
  }, [onChange]);

  // cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Tooltip title={isFullscreen ? "전체화면 종료 (ESC)" : "전체화면"}>
          <IconButton size="small" onClick={onToggleFullscreen}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          bgcolor: 'background.paper',
          p: 2,
          display: 'flex',
          flexDirection: 'column'
        })
      }}>
        {isFullscreen && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6">{label}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={onToggleFullscreen}
              >
                취소
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onSave}
              >
                수정
              </Button>
              <IconButton onClick={onToggleFullscreen}>
                <FullscreenExitIcon />
              </IconButton>
            </Box>
          </Box>
        )}
        <TextField
          multiline
          rows={isFullscreen ? undefined : rows}
          fullWidth
          value={localValue}
          onChange={handleChange}
          placeholder={`${label}을(를) 입력하세요...`}
          sx={{
            flex: isFullscreen ? 1 : 'auto',
            ...(isFullscreen && {
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }),
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: isFullscreen ? '16px' : '14px',
              lineHeight: 1.6,
              bgcolor: '#1e1e1e',
              color: '#d4d4d4',
              ...(isFullscreen && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }),
              '& textarea': {
                color: '#d4d4d4',
                ...(isFullscreen && {
                  flex: 1,
                  overflow: 'auto !important',
                  height: '100% !important'
                })
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#555'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#777'
            }
          }}
        />
      </Box>
    </Box>
  );
});

const PromptManagementPage = () => {
  const [prompts, setPrompts] = useState([]);
  const [filePrompts, setFilePrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [fullscreenField, setFullscreenField] = useState(null);
  const [pythonCode, setPythonCode] = useState('');
  const [pythonCodeLoading, setPythonCodeLoading] = useState(false);
  const [pythonCodePath, setPythonCodePath] = useState('');
  const [pythonCodeEditable, setPythonCodeEditable] = useState(false);
  const [pythonCodeSource, setPythonCodeSource] = useState('');
  const [pythonCodeSaving, setPythonCodeSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, prompt: null, deleting: false });

  const [formData, setFormData] = useState({
    prompt_id: '',
    prompt_name: '',
    reference_sheet_names: '',
    exclude_sheet_keywords: '',
    convert_prompt: '',
    validation_prompt: '',
    execution_method: 'PY',
    is_active: true
  });

  const { selectedTenant, getSelectedTenantInfo, tenants, setSelectedTenant } = useGlobalTenant();

  // ESC 키로 전체화면 종료
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && fullscreenField) {
        setFullscreenField(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [fullscreenField]);

  // 데이터 조회
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 선택된 테넌트의 tenant_code 추출
      const tenantInfo = getSelectedTenantInfo();
      if (!tenantInfo) {
        console.warn('⚠️ 테넌트가 선택되지 않았습니다');
        setLoading(false);
        return;
      }
      const tenantCode = tenantInfo.tenant_code;

      // 1. 프롬프트 목록 조회
      const promptsResponse = await apiService.getPrompts(tenantCode);
      if (promptsResponse.success) {
        setPrompts(promptsResponse.data);
      }

      // 2. 파일-프롬프트 연결 정보 조회
      const filePromptsResponse = await apiService.getFilePrompts(tenantCode);
      if (filePromptsResponse.success) {
        setFilePrompts(filePromptsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTenant]);

  // 프롬프트가 사용되는 파일 수 계산
  const getFileCount = (promptId) => {
    return filePrompts.filter(fp => fp.prompt_id === promptId).length;
  };

  // 프롬프트를 사용하는 테넌트 목록
  const getTenantsUsingPrompt = (promptId) => {
    const tenantCodes = [...new Set(
      filePrompts
        .filter(fp => fp.prompt_id === promptId)
        .map(fp => fp.tenant_code)
    )];

    return tenants.filter(t => tenantCodes.includes(t.tenant_code));
  };

  // Python 코드 로드
  const loadPythonCode = async (tenantCode, promptId) => {
    setPythonCodeLoading(true);
    setPythonCode('');
    setPythonCodePath('');
    setPythonCodeEditable(false);
    setPythonCodeSource('');
    try {
      const response = await apiService.getPythonCode(tenantCode, promptId);
      if (response.success) {
        setPythonCode(response.data.code);
        setPythonCodePath(response.data.path);
        setPythonCodeEditable(response.data.editable || false);
        setPythonCodeSource(response.source || '');
      }
    } catch (error) {
      console.error('Python 코드 로드 실패:', error);
      setPythonCode('// Python 파일을 찾을 수 없습니다.');
      setPythonCodePath(`gs://hyperlounge-${tenantCode}-foldersync/AIFYAGENT_INPUT/converters/${promptId}/script.py`);
      setPythonCodeEditable(false);
      setPythonCodeSource('');
    } finally {
      setPythonCodeLoading(false);
    }
  };

  // Python 코드 저장 (테넌트 버킷으로 복사 포함)
  const handleSavePythonCode = async () => {
    if (!selectedPrompt || !pythonCode) return;

    setPythonCodeSaving(true);
    try {
      const response = await apiService.savePythonCode(
        selectedPrompt.tenant_code,
        selectedPrompt.prompt_id,
        pythonCode
      );
      if (response.success) {
        setSuccess('Python 코드가 테넌트 버킷에 저장되었습니다.');
        setPythonCodePath(response.data.path);
        // 저장 후 Python 코드 다시 로드하여 editable 상태 업데이트
        setPythonCodeEditable(true);
        setPythonCodeSource('tenant_bucket');
      }
    } catch (error) {
      console.error('Python 코드 저장 실패:', error);
      setError('Python 코드 저장에 실패했습니다: ' + error.message);
    } finally {
      setPythonCodeSaving(false);
    }
  };

  // 다이얼로그 열기
  const handleOpenDialog = (prompt = null) => {
    if (prompt) {
      setSelectedPrompt(prompt);
      setFormData({
        prompt_id: prompt.prompt_id,
        tenant_code: prompt.tenant_code || '',
        prompt_name: prompt.prompt_name || '',
        reference_sheet_names: Array.isArray(prompt.reference_sheet_names)
          ? prompt.reference_sheet_names.join(', ')
          : (prompt.reference_sheet_names || ''),
        exclude_sheet_keywords: Array.isArray(prompt.exclude_sheet_keywords)
          ? prompt.exclude_sheet_keywords.join(', ')
          : (prompt.exclude_sheet_keywords || ''),
        convert_prompt: prompt.convert_prompt || '',
        validation_prompt: prompt.validation_prompt || '',
        is_active: prompt.is_active !== false
      });
      // Python 코드 로드
      loadPythonCode(prompt.tenant_code, prompt.prompt_id);
    } else {
      setSelectedPrompt(null);
      const tenantInfo = getSelectedTenantInfo();
      setFormData({
        prompt_id: '',
        tenant_code: tenantInfo?.tenant_code || '',
        prompt_name: '',
        reference_sheet_names: '',
        exclude_sheet_keywords: '',
        convert_prompt: '',
        validation_prompt: '',
        is_active: true
      });
      setPythonCode('');
      setPythonCodePath('');
      setPythonCodeEditable(false);
      setPythonCodeSource('');
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
    setTabValue(0);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPrompt(null);
    setError(null);
    setSuccess(null);
    setFullscreenField(null);
  };

  // 입력 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 에디터 변경 처리
  const handleEditorChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 전체화면 토글
  const toggleFullscreen = (field) => {
    setFullscreenField(fullscreenField === field ? null : field);
  };

  // 저장
  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // 필수 필드 검증
    if (!formData.prompt_id || !formData.prompt_name) {
      setError('프롬프트 ID와 이름은 필수 입력 항목입니다.');
      return;
    }

    try {
      // 선택된 테넌트 정보 추출
      const tenantInfo = getSelectedTenantInfo();
      const currentTenantCode = tenantInfo.tenant_code;

      // 수정 시에는 원래 프롬프트의 tenant_code 사용, 생성 시에는 선택된 tenant 사용
      const tenantCode = selectedPrompt ? selectedPrompt.tenant_code : currentTenantCode;

      // 'all'이 선택된 상태에서 새 프롬프트 생성하는 경우 에러 처리
      if (!selectedPrompt && currentTenantCode === 'all') {
        setError('프롬프트를 생성하려면 특정 테넌트를 선택해주세요.');
        return;
      }

      // reference_sheet_names와 exclude_sheet_keywords를 배열로 변환
      const referenceSheetNames = formData.reference_sheet_names
        ? formData.reference_sheet_names.split(',').map(s => s.trim()).filter(s => s)
        : [];

      const excludeSheetKeywords = formData.exclude_sheet_keywords
        ? formData.exclude_sheet_keywords.split(',').map(s => s.trim()).filter(s => s)
        : [];

      const promptData = {
        tenant_code: tenantCode,
        prompt_id: formData.prompt_id,
        prompt_name: formData.prompt_name,
        reference_sheet_names: referenceSheetNames,
        exclude_sheet_keywords: excludeSheetKeywords,
        convert_prompt: formData.convert_prompt,
        validation_prompt: formData.validation_prompt,
        is_active: formData.is_active,
        created_by: 'user',
        updated_by: 'user'
      };

      let response;
      if (selectedPrompt) {
        // 수정
        response = await apiService.updatePrompt(tenantCode, formData.prompt_id, promptData);
      } else {
        // 생성
        response = await apiService.createPrompt(promptData);
      }

      if (response.success) {
        setSuccess(selectedPrompt ? '프롬프트가 수정되었습니다.' : '프롬프트가 생성되었습니다.');
        fetchData();

        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      } else {
        setError(response.error || '프롬프트 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError(error.message || '프롬프트 저장에 실패했습니다.');
    }
  };

  // 삭제 다이얼로그 열기
  const handleDeleteClick = (prompt) => {
    setDeleteDialog({ open: true, prompt, deleting: false });
  };

  // 삭제 확인
  const handleDeleteConfirm = async () => {
    const prompt = deleteDialog.prompt;
    if (!prompt) return;

    setDeleteDialog(prev => ({ ...prev, deleting: true }));

    try {
      const tenantCodeToUse = prompt.tenant_code || selectedTenant;
      const response = await apiService.deletePrompt(tenantCodeToUse, prompt.prompt_id);

      if (response.success) {
        const deletedFilesCount = response.deleted_files?.length || 0;
        const message = response.bucket_deleted
          ? `프롬프트와 버킷 파일(${deletedFilesCount}개)이 삭제되었습니다.`
          : '프롬프트가 삭제되었습니다.';
        setSuccess(message);
        setDeleteDialog({ open: false, prompt: null, deleting: false });
        fetchData();

        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(response.error || '프롬프트 삭제에 실패했습니다.');
        setDeleteDialog(prev => ({ ...prev, deleting: false }));
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError(error.message || '프롬프트 삭제에 실패했습니다.');
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };

  // 삭제 취소
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, prompt: null, deleting: false });
  };

  // handleEditorChange를 useCallback으로 메모이제이션
  const handleEditorChangeCallback = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            프롬프트 관리
          </Typography>
          <Typography variant="body2" color="text.secondary">
            재사용 가능한 프롬프트를 관리하고 여러 파일에 적용할 수 있습니다
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            새로고침
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            새 프롬프트 추가
          </Button>
        </Box>
      </Box>

      {/* 테넌트 선택 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>테넌트 선택</InputLabel>
          <Select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            label="테넌트 선택"
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.tenant_name} ({tenant.tenant_codes?.find(tc => tc.is_primary)?.tenant_code || tenant.tenant_codes?.[0]?.tenant_code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 에러/성공 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                전체 프롬프트
              </Typography>
              <Typography variant="h4">
                {prompts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                활성 프롬프트
              </Typography>
              <Typography variant="h4">
                {prompts.filter(p => p.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                사용 중인 프롬프트
              </Typography>
              <Typography variant="h4">
                {prompts.filter(p => getFileCount(p.prompt_id) > 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 프롬프트 테이블 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>프롬프트 ID</TableCell>
                <TableCell>프롬프트 이름</TableCell>
                <TableCell>사용 중인 파일</TableCell>
                <TableCell>적용된 고객사</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>작성자</TableCell>
                <TableCell>최종 수정일</TableCell>
                <TableCell align="right">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      프롬프트가 없습니다. 새 프롬프트를 추가해주세요.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                prompts.map((prompt) => {
                  const tenantsUsing = getTenantsUsingPrompt(prompt.prompt_id);
                  return (
                    <TableRow key={prompt.prompt_id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CodeIcon fontSize="small" color="primary" />
                          {prompt.prompt_id}
                        </Box>
                      </TableCell>
                      <TableCell>{prompt.prompt_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${getFileCount(prompt.prompt_id)}개 파일`}
                          size="small"
                          color={getFileCount(prompt.prompt_id) > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {prompt.tenant_code ? (
                            <Chip
                              icon={<BusinessIcon />}
                              label={prompt.tenant_code}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prompt.is_active ? '활성' : '비활성'}
                          size="small"
                          color={prompt.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{prompt.created_by || '-'}</TableCell>
                      <TableCell>
                        {prompt.updated_at ? new Date(prompt.updated_at).toLocaleDateString('ko-KR') : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(prompt)}
                          title="수정"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(prompt)}
                          title="삭제"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 프롬프트 편집 다이얼로그 */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          {selectedPrompt ? '프롬프트 수정' : '새 프롬프트 추가'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2, mt: 2 }}>
            <Tab label="기본 정보" />
            <Tab label="프롬프트" />
            <Tab label="Python 코드" />
          </Tabs>

          {/* 탭 0: 기본 정보 */}
          {tabValue === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="프롬프트 ID"
                name="prompt_id"
                value={formData.prompt_id}
                onChange={handleChange}
                fullWidth
                required
                disabled={!!selectedPrompt}
                placeholder="예: default, v2_monthly, transform_basic"
                helperText={!!selectedPrompt ? "프롬프트 ID는 기본 키로 수정할 수 없습니다. 변경이 필요하면 새 프롬프트를 생성해주세요." : "영문, 숫자, 언더스코어만 사용"}
              />

              <Box>
                <TextField
                  label="테넌트 코드 (고객사)"
                  name="tenant_code"
                  value={formData.tenant_code}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={!!selectedPrompt}
                  placeholder="예: cf526000, c8cd3500"
                  helperText={
                    !!selectedPrompt
                      ? "테넌트 코드는 기본 키의 일부로 수정할 수 없습니다. 변경이 필요하면 새 프롬프트를 생성해주세요."
                      : "이 프롬프트가 속할 고객사의 테넌트 코드"
                  }
                />
                {formData.tenant_code && tenants.find(t =>
                  t.tenant_codes?.some(tc => tc.tenant_code === formData.tenant_code)
                ) && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      고객사: {tenants.find(t =>
                        t.tenant_codes?.some(tc => tc.tenant_code === formData.tenant_code)
                      ).tenant_name}
                    </Typography>
                  </Box>
                )}
              </Box>

              <TextField
                label="프롬프트 이름"
                name="prompt_name"
                value={formData.prompt_name}
                onChange={handleChange}
                fullWidth
                required
                placeholder="예: 기본 변환 프롬프트"
                helperText="사용자가 이해하기 쉬운 이름"
              />

              <FormControl fullWidth required>
                <InputLabel>수행방법</InputLabel>
                <Select
                  name="execution_method"
                  value={formData.execution_method || 'PY'}
                  onChange={handleChange}
                  label="수행방법"
                >
                  <MenuItem value="PY">Python 스크립트 변환</MenuItem>
                  <MenuItem value="SO">Claude Sonnet 변환</MenuItem>
                  <MenuItem value="HA">Claude Haiku 변환</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  PY: 빠른 Python 변환, SO: Sonnet (고품질), HA: Haiku (고속)
                </Typography>
              </FormControl>

              <TextField
                label="참조 시트명"
                name="reference_sheet_names"
                value={formData.reference_sheet_names}
                onChange={handleChange}
                fullWidth
                placeholder="예: 가동률(종합), 가동률(이촌점)"
                helperText="Excel 파일에서 필터링할 시트 이름 (쉼표로 구분)"
              />

              <TextField
                label="제외 키워드"
                name="exclude_sheet_keywords"
                value={formData.exclude_sheet_keywords}
                onChange={handleChange}
                fullWidth
                placeholder="예: 로그북, 통합"
                helperText="시트 이름에서 제외할 키워드 (쉼표로 구분)"
              />
            </Box>
          )}

          {/* 탭 1: 프롬프트 */}
          {tabValue === 1 && (
            <Box>
              {/* 버킷 경로 안내 */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>저장 위치:</strong> <code>gs://hyperlounge-{selectedPrompt?.tenant_code || formData.tenant_code || '{tenant_code}'}-foldersync/AIFYAGENT_INPUT/converters/{selectedPrompt?.prompt_id || formData.prompt_id || '{prompt_id}'}/</code><br />
                  <strong>변환 프롬프트:</strong> <code>convert_prompt.md</code><br />
                  <strong>검증 프롬프트:</strong> <code>validation_prompt.md</code>
                </Typography>
              </Alert>

              <CodeEditorField
                label="변환 프롬프트"
                field="convert_prompt"
                value={formData.convert_prompt || ''}
                onChange={(value) => handleEditorChangeCallback('convert_prompt', value)}
                isFullscreen={fullscreenField === 'convert_prompt'}
                onToggleFullscreen={() => toggleFullscreen('convert_prompt')}
                onSave={handleSave}
                rows={10}
              />
              <CodeEditorField
                label="검증 프롬프트"
                field="validation_prompt"
                value={formData.validation_prompt || ''}
                onChange={(value) => handleEditorChangeCallback('validation_prompt', value)}
                isFullscreen={fullscreenField === 'validation_prompt'}
                onToggleFullscreen={() => toggleFullscreen('validation_prompt')}
                onSave={handleSave}
                rows={10}
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>변환 프롬프트:</strong> Excel 데이터를 변환하는 로직을 정의하는 프롬프트<br />
                  <strong>검증 프롬프트:</strong> 변환 결과를 검증하는 프롬프트<br />
                  <strong>저장:</strong> 하단 [수정] 버튼 클릭 시 테넌트 버킷에 저장됩니다.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* 탭 2: Python 코드 */}
          {tabValue === 2 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>저장 위치:</strong> <code>gs://hyperlounge-{selectedPrompt?.tenant_code || formData.tenant_code || '{tenant_code}'}-foldersync/AIFYAGENT_INPUT/converters/{selectedPrompt?.prompt_id || formData.prompt_id || '{prompt_id}'}/script.py</code><br />
                  <strong>편집:</strong> 아래 에디터에서 직접 수정 후 [Python 코드 저장] 버튼 클릭
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {pythonCodePath || `gs://hyperlounge-${selectedPrompt?.tenant_code || formData.tenant_code || '{tenant_code}'}-foldersync/AIFYAGENT_INPUT/converters/${selectedPrompt?.prompt_id || formData.prompt_id || '{prompt_id}'}/script.py`}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSavePythonCode}
                  disabled={pythonCodeSaving || !pythonCode}
                  startIcon={pythonCodeSaving ? <CircularProgress size={16} /> : null}
                >
                  {pythonCodeSaving ? '저장 중...' : 'Python 코드 저장'}
                </Button>
              </Box>
              {pythonCodeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <CodeEditorField
                  label="Python 변환 스크립트"
                  field="python_code"
                  value={pythonCode || '// Python 파일이 로드되지 않았습니다.'}
                  onChange={(value) => setPythonCode(value)}
                  isFullscreen={fullscreenField === 'python_code'}
                  onToggleFullscreen={() => toggleFullscreen('python_code')}
                  onSave={handleSavePythonCode}
                  rows={20}
                />
              )}
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedPrompt ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          🗑️ 컨버터 삭제 확인
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {deleteDialog.prompt && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                이 작업은 취소할 수 없습니다. DB와 버킷에서 모두 삭제됩니다.
              </Alert>

              <Typography variant="subtitle1" gutterBottom>
                <strong>삭제할 컨버터:</strong>
              </Typography>

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>ID:</strong> {deleteDialog.prompt.prompt_id}
                </Typography>
                <Typography variant="body2">
                  <strong>이름:</strong> {deleteDialog.prompt.prompt_name || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>테넌트:</strong> {deleteDialog.prompt.tenant_code}
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                삭제되는 파일:
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                <Box>• AIFYAGENT_INPUT/converters/{deleteDialog.prompt.prompt_id}/config.json</Box>
                <Box>• AIFYAGENT_INPUT/converters/{deleteDialog.prompt.prompt_id}/convert_prompt.md</Box>
                <Box>• AIFYAGENT_INPUT/converters/{deleteDialog.prompt.prompt_id}/validation_prompt.md</Box>
                <Box>• AIFYAGENT_INPUT/converters/{deleteDialog.prompt.prompt_id}/script.py</Box>
              </Box>

              {getFileCount(deleteDialog.prompt.prompt_id) > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  ⚠️ 이 프롬프트는 {getFileCount(deleteDialog.prompt.prompt_id)}개의 파일에서 사용 중입니다!
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteDialog.deleting}>
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteDialog.deleting}
            startIcon={deleteDialog.deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteDialog.deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromptManagementPage;
